import { ChevronLeft, ChevronRight } from 'lucide-react'
import * as React from 'react'
import { DayPicker, DropdownProps } from 'react-day-picker'

import { buttonVariants } from '~in-app/components/shadcn-ui/button'
import { cn } from '~in-app/lib/utils'

import { ScrollArea } from './scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select'

export type CalendarProps = React.ComponentProps<typeof DayPicker>

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      className={cn('p-3', className)}
      showOutsideDays={showOutsideDays}
      classNames={
        {
          months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
          month: 'space-y-4',
          caption: 'flex justify-center pt-1 relative items-center',
          caption_label: 'hidden text-sm font-medium',
          caption_dropdowns: 'flex justify-center gap-1',
          nav: 'space-x-1 flex items-center',
          nav_button: cn(
            buttonVariants({ variant: 'outlined' }),
            'h-7 w-7 text-muted-foreground bg-transparent p-0 opacity-100 hover:opacity-50',
          ),
          nav_button_previous: 'absolute left-1 text-muted-foreground',
          nav_button_next: 'absolute right-1',
          table: 'w-full border-collapse space-y-1',
          head_row: 'flex',
          head_cell:
          'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
          row: 'flex w-full mt-2',
          cell: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
          day: cn(
            buttonVariants({ variant: 'outlined' }),
            'h-9 w-9 p-0 font-normal aria-selected:text-primary-foreground aria-selected:font-bold aria-selected:bg-primary text-muted-foreground hover:text-primary hover:opacity-50',
          ),
          day_range_end: 'day-range-end',
          day_selected:
          'bg-primary font-bold hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
          day_today: 'bg-accent text-accent-foreground',
          day_outside:
          'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
          day_disabled: 'text-muted-foreground opacity-50',
          day_range_middle:
          'aria-selected:bg-accent aria-selected:text-accent-foreground',
          day_hidden: 'invisible',
          ...classNames,
        }
      }
      components={
        {
          Dropdown: ({ value, onChange, children, ...props }: DropdownProps) => {
            const options = React.Children.toArray(children) as React.ReactElement<React.HTMLProps<HTMLOptionElement>>[]
            const selected = options.find(child => child.props.value === value)
            const handleChange = (value: string) => {
              const changeEvent = {
                target: { value },
              } as React.ChangeEvent<HTMLSelectElement>
              onChange?.(changeEvent)
            }
            return (
              <Select
                value={value?.toString()}
                onValueChange={
                  (value) => {
                    handleChange(value)
                  }
                }
              >
                <SelectTrigger className="pr-1.5 focus:ring-0">
                  <SelectValue>{selected?.props?.children}</SelectValue>
                </SelectTrigger>

                <SelectContent
                  className="bg-white dark:bg-gray-800"
                  position="popper"
                >
                  <ScrollArea className="h-80">
                    {
                      options.map((option, id: number) => (
                        <SelectItem
                          key={`${option.props.value}-${id}`}
                          value={option.props.value?.toString() ?? ''}
                        >
                          {option.props.children}
                        </SelectItem>
                      ))
                    }
                  </ScrollArea>
                </SelectContent>
              </Select>
            )
          },
          IconLeft: ({ ...props }) => <ChevronLeft className="size-4" />,
          IconRight: ({ ...props }) => <ChevronRight className="size-4" />,
        }
      }
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
